import React, { Key } from "react";

import { queryCache, useMutation, useQuery } from "react-query";

import { Vero1ConflictCode } from "manager/components/vero1/Vero1ReviewPanel/persistItem";
import { APPLICATION_AUDITS_QUERY } from "manager/config/constants";
import { COSIGN_INVITATION_ELIGIBILITY } from "manager/hooks/api/cosignQueries";
import {
  GET_DEAL_KEY,
  GET_DEALS_COUNT_KEY,
  GET_DEALS_FAST_KEY,
  GET_DEALS_KEY,
} from "manager/hooks/api/dealQueries";
import { NOTIFICATIONS } from "shared/config/constants";
import type {
  Vero1ChecklistItem as Vero1ChecklistItemPayload,
  Vero1ChecklistPayload,
  Vero1CustomItemInput,
  Vero1ItemEditRequest,
  Vero1SubmitInput,
} from "shared/interfaces/api/vero1";
import { Vero1GenerationStatus } from "shared/interfaces/api/vero1";
import {
  addVero1ChecklistItem,
  carryForwardVero1Checklist,
  deleteVero1ChecklistItem,
  discardVero1Checklist,
  editVero1ChecklistItem,
  editVero1ChecklistItemSlim,
  getDealLumenRuns,
  getVero1Checklist,
  getVero1ChecklistVersion,
  getVero1ChecklistVersions,
  refreshVero1Checklist,
  startVero1Checklist,
  submitVero1Checklist,
} from "shared/lib/api/vero1Api";
import { openNotification } from "shared/utils/ui";

export const GET_VERO1_CHECKLIST_KEY = "getVero1Checklist";
const GET_VERO1_VERSIONS_KEY = "getVero1ChecklistVersions";
const GET_VERO1_VERSION_KEY = "getVero1ChecklistVersion";
const GET_DEAL_LUMEN_RUNS_KEY = "getDealLumenRuns";

export const VERO1_QUERY_KEYS = {
  getVero1Checklist: (dealId: Key) => [
    GET_VERO1_CHECKLIST_KEY,
    { dealId: String(dealId) },
  ],
  getVero1ChecklistVersions: (dealId: Key) => [
    GET_VERO1_VERSIONS_KEY,
    { dealId: String(dealId) },
  ],
  getVero1ChecklistVersion: (dealId: Key, version: number) => [
    GET_VERO1_VERSION_KEY,
    { dealId: String(dealId), version },
  ],
  getDealLumenRuns: (dealId: Key) => [
    GET_DEAL_LUMEN_RUNS_KEY,
    { dealId: String(dealId) },
  ],
};

/**
 * How often the open panel re-asks Lumen's side of the world.
 *
 * Screening is not push: nothing tells the panel a run landed. The staleness
 * flag that would say "refresh me" only arrives ON a fetch — so a poll gated
 * on already-knowing-it's-stale can never learn anything new. While the panel
 * is on screen it polls unconditionally; closed surfaces (the banner trigger)
 * ask nothing.
 */
const PENDING_POLL_MS = 15000;

// one toast per failure STREAK, per deal: the poll retries every 15s, and a
// sustained outage repeating the same toast for the rest of the session tells
// the agent nothing new. A success re-arms it so the next outage speaks once.
const failureToasted = new Set<Key>();

/**
 * A read that resolves after a newer write must not be applied.
 *
 * The poll and every mutation write the same cache key, so an in-flight GET
 * landing after a save regresses the panel to the pre-save checklist — the
 * agent watches their own entry disappear until the next tick. The draft's
 * updated_at is monotonic, so anything strictly older than what is cached is
 * stale by definition.
 */
const freshest = (
  incoming: Vero1ChecklistPayload,
  cached?: Vero1ChecklistPayload
) => {
  const incomingAt = incoming?.checklist?.updatedAt;
  const cachedAt = cached?.checklist?.updatedAt;
  if (!incomingAt || !cachedAt) return incoming;
  // same draft only — a different checklist id is a new review, not a regress
  if (String(incoming.checklist?.id) !== String(cached.checklist?.id)) {
    return incoming;
  }
  if (new Date(incomingAt) >= new Date(cachedAt)) return incoming;
  // an older read still carries current run news: it was answered by the same
  // server, and generation/staleness describe Lumen, not the draft. Dropping
  // the whole payload would lose a landed run until the next tick.
  //
  // Staleness is the exception: it is a verdict about ONE pin. A poll that
  // left before a refresh re-pinned the draft answers for the old pin, so
  // adopting its true would re-arm the auto-refresh against a pin already
  // current — burning that pin's once-only guard and clearing the agent's
  // entries a second time. Keep the cached verdict when the pin moved.
  const samePin = incoming.checklist?.baseRunId === cached.checklist?.baseRunId;
  return {
    ...cached,
    generation: incoming.generation ?? cached.generation,
    baseRunStale: samePin
      ? incoming.baseRunStale ?? cached.baseRunStale
      : cached.baseRunStale,
  };
};

/** The 409 a restart takes when the latest run is the review's own derived
 *  run — the backend kicked a fresh screening; expected, not a failure. */
/** A 409 meaning "a run has to finish first" — either the deal's own run was
 *  already in flight, or the agent's restart just kicked a fresh one. Both hold
 *  the panel the same way; only the wording differs. */
const RUN_PENDING_STATES = ["run-in-progress", "rescreening-for-review"];

export const isRunInProgressConflict = (error: {
  statusCode?: number;
  errors?: { state?: string };
  response?: { status?: number; data?: { state?: string } };
}) =>
  (error?.statusCode ?? error?.response?.status) === 409 &&
  RUN_PENDING_STATES.includes(
    (error?.errors?.state ?? error?.response?.data?.state) as string
  );

const vero1SettlingCounts = new Map<string, number>();
const vero1SettlingListeners = new Set<() => void>();
const notifyVero1Settling = () => {
  vero1SettlingListeners.forEach((listener) => listener());
};
export const enterVero1Settling = (dealId: Key) => {
  const id = String(dealId);
  vero1SettlingCounts.set(id, (vero1SettlingCounts.get(id) ?? 0) + 1);
  notifyVero1Settling();
};
export const exitVero1Settling = (dealId: Key) => {
  const id = String(dealId);
  const next = (vero1SettlingCounts.get(id) ?? 0) - 1;
  if (next > 0) {
    vero1SettlingCounts.set(id, next);
  } else {
    vero1SettlingCounts.delete(id);
  }
  notifyVero1Settling();
};
export const useVero1StatusSettling = (dealId?: Key | null): boolean => {
  const [, force] = React.useReducer((c: number) => c + 1, 0);
  React.useEffect(() => {
    vero1SettlingListeners.add(force);
    return () => {
      vero1SettlingListeners.delete(force);
    };
  }, []);
  if (dealId == null) return false;
  return (vero1SettlingCounts.get(String(dealId)) ?? 0) > 0;
};

/** Every read the deal page derives VERO1 status from — one invalidation
 *  set shared by all status-moving writes. */
const invalidateVero1StatusReads = (dealId: Key) =>
  Promise.all([
    queryCache.invalidateQueries("dealApplicationsSummaryDeal"),
    queryCache.invalidateQueries("dealApplicationsSummary"),
    queryCache.invalidateQueries(GET_DEAL_KEY),
    queryCache.invalidateQueries("getLumenResultForDeal"),
    queryCache.invalidateQueries("getLatestLumenV2Run"),
    queryCache.invalidateQueries(
      VERO1_QUERY_KEYS.getVero1ChecklistVersions(dealId)
    ),
    queryCache.invalidateQueries(GET_VERO1_VERSION_KEY),
    queryCache.invalidateQueries(VERO1_QUERY_KEYS.getDealLumenRuns(dealId)),
    queryCache.invalidateQueries([APPLICATION_AUDITS_QUERY, dealId]),
    queryCache.invalidateQueries(COSIGN_INVITATION_ELIGIBILITY),
    // keep the deals-list VERO1 recommendation cell fresh (PRODUCT-2062)
    queryCache.invalidateQueries(GET_DEALS_KEY),
    queryCache.invalidateQueries(GET_DEALS_FAST_KEY),
    queryCache.invalidateQueries(GET_DEALS_COUNT_KEY),
  ]);

const isPending = (data?: Vero1ChecklistPayload) =>
  // a run is in flight, or one landed under an open draft and the refresh has
  // not been applied yet
  data?.generation?.state === Vero1GenerationStatus.RunInProgress ||
  Boolean(data?.baseRunStale);

export const useGetVero1Checklist = (
  dealId: Key,
  enabled = true,
  /** The open panel passes true: poll every 15s so a run landing while the
   *  agent works is noticed without any agent action. Off-panel consumers (the
   *  banner trigger) leave it false, still polling a known pending state. */
  pollWhileOpen = false
) => {
  const cached = queryCache.getQueryData<Vero1ChecklistPayload>(
    VERO1_QUERY_KEYS.getVero1Checklist(dealId)
  );
  const { data, isLoading, refetch } = useQuery<Vero1ChecklistPayload>(
    VERO1_QUERY_KEYS.getVero1Checklist(dealId),
    async () =>
      freshest(
        await getVero1Checklist(dealId),
        queryCache.getQueryData<Vero1ChecklistPayload>(
          VERO1_QUERY_KEYS.getVero1Checklist(dealId)
        )
      ),
    {
      enabled: enabled && Boolean(dealId),
      // React Query v2 takes a value, not a predicate — recomputed from the
      // cached payload on each render, so pending polling stops on its own
      // the moment nothing is pending.
      refetchInterval:
        (pollWhileOpen && enabled) || isPending(cached)
          ? PENDING_POLL_MS
          : false,
      onSuccess: () => {
        failureToasted.delete(dealId);
      },
      retry: (failureCount: number, err: any) =>
        (err?.statusCode ?? err?.response?.status) !== 404 && failureCount < 3,
      onError: (err: any) => {
        if ((err?.statusCode ?? err?.response?.status) === 404) return;
        if (failureToasted.has(dealId)) return;
        failureToasted.add(dealId);
        openNotification(
          "Failed to load the VERO1 review",
          NOTIFICATIONS.error
        );
      },
    }
  );

  return {
    checklist: data?.checklist ?? null,
    reviewInProgress: Boolean(data?.reviewInProgress),
    generation: data?.generation,
    reasonCatalog: data?.reasonCodes,
    persistScopeOptions: data?.persistScopeOptions,
    canAddField: Boolean(data?.canAddField),
    baseRunStale: Boolean(data?.baseRunStale),
    rereview: data?.rereview ?? null,
    isChecklistLoading: isLoading,
    refetchChecklist: refetch,
  };
};

/** Deal review tab (PRODUCT-2060): every submitted version, newest first.
 *  Agent-only server-side — the 403 for leasing is expected, not an error. */
export const useVero1ChecklistVersions = (dealId: Key, enabled = true) => {
  const { data, isLoading } = useQuery(
    VERO1_QUERY_KEYS.getVero1ChecklistVersions(dealId),
    () => getVero1ChecklistVersions(dealId),
    {
      enabled: enabled && Boolean(dealId),
      retry: (failureCount: number, err: any) =>
        ![403, 404].includes(err?.statusCode ?? err?.response?.status) &&
        failureCount < 3,
    }
  );
  return { versions: data ?? [], versionsLoading: isLoading };
};

/** One submitted version's full agent payload — fetched on picker selection. */
export const useVero1ChecklistVersion = (
  dealId: Key,
  version: number | null,
  enabled = true
) => {
  const { data, isLoading } = useQuery(
    VERO1_QUERY_KEYS.getVero1ChecklistVersion(dealId, version ?? -1),
    () => getVero1ChecklistVersion(dealId, version as number),
    { enabled: enabled && Boolean(dealId) && version != null }
  );
  return { versionDetail: data ?? null, versionLoading: isLoading };
};

/** Run history for the Lumen screening section — lumen-service summary rows
 *  joined with provenance, newest first. */
export const useDealLumenRuns = (dealId: Key, enabled = true) => {
  const { data, isLoading, isError } = useQuery(
    VERO1_QUERY_KEYS.getDealLumenRuns(dealId),
    () => getDealLumenRuns(dealId),
    {
      enabled: enabled && Boolean(dealId),
      retry: (failureCount: number, err: any) =>
        ![403, 404].includes(err?.statusCode ?? err?.response?.status) &&
        failureCount < 2,
    }
  );
  return { runs: data ?? [], runsLoading: isLoading, runsUnavailable: isError };
};

/**
 * Every write returns the whole checklist, so the read query is seeded from the
 * response rather than refetched. Submit additionally moves the deal's
 * recommendation, so that query is invalidated too.
 */
const useChecklistMutation = <TInput>(
  dealId: Key,
  request: (input: TInput) => Promise<unknown>,
  {
    errorMessage,
    invalidatesDeal = false,
    awaitDerivedRun = false,
    successMessage = "",
    conflictHandledInline = false,
    validationHandledInline = false,
  }: {
    errorMessage: string;
    invalidatesDeal?: boolean;
    awaitDerivedRun?: boolean;
    successMessage?: string;
    /** The caller renders its own conflict UI (spec 4.4 / 6.6) — refetch the
     *  draft but skip the generic re-apply toast. */
    conflictHandledInline?: boolean;
    /** The caller renders 400 validation rejections inline (duplicate label,
     *  already-included) — skip the generic failure toast for those. */
    validationHandledInline?: boolean;
  }
) => {
  const key = VERO1_QUERY_KEYS.getVero1Checklist(dealId);

  return useMutation(request, {
    onSuccess: async (checklist) => {
      if (checklist) {
        queryCache.setQueryData(
          key,
          (prev: Vero1ChecklistPayload | undefined) =>
            freshest(
              {
                ...(prev || { reviewInProgress: false }),
                checklist,
                // a response that re-pins the draft (refresh) makes the cached
                // staleness verdict obsolete — leaving it true fires a redundant
                // auto-refresh that burns the once-per-pin guard for the new pin
                ...(prev?.checklist &&
                (checklist as Vero1ChecklistPayload["checklist"])?.baseRunId !==
                  prev.checklist.baseRunId
                  ? { baseRunStale: false }
                  : {}),
              } as Vero1ChecklistPayload,
              prev
            )
        );
      } else {
        // discard returns nothing: clear the cached draft NOW so it cannot
        // flash back while the refetch is in flight
        queryCache.setQueryData(
          key,
          (prev: Vero1ChecklistPayload | undefined) => ({
            ...(prev || {}),
            checklist: null,
            reviewInProgress: false,
          })
        );
        queryCache.invalidateQueries(key);
      }
      // every checklist mutation writes a V1C_* SystemAudit, so the Activity
      // tab is stale the moment one lands
      queryCache.invalidateQueries([APPLICATION_AUDITS_QUERY, dealId]);
      // version history moves on submit/discard/start (supersede, withdraw,
      // new draft) — cheap to refresh on every write rather than enumerate;
      // statuses on PRIOR versions move too (v15 -> superseded on submit)
      queryCache.invalidateQueries(
        VERO1_QUERY_KEYS.getVero1ChecklistVersions(dealId)
      );
      queryCache.invalidateQueries(GET_VERO1_VERSION_KEY);
      if (invalidatesDeal) {
        // awaited so the deal summary reflects the write before the panel
        // closes onto it; the banner skeletons for exactly this window
        enterVero1Settling(dealId);
        try {
          if (awaitDerivedRun) {
            // wait for the run identity to move — refetching before the
            // derived run lands resolves the skeleton onto stale numbers
            for (let attempt = 0; attempt < 8; attempt += 1) {
              let settled = false;
              try {
                // eslint-disable-next-line no-await-in-loop
                const fresh = await getVero1Checklist(dealId);
                const latestRun = fresh?.generation?.latestRunId;
                const reviewedBase = fresh?.checklist?.baseRunId;
                // no checklist (discard) leaves no run to move off
                settled =
                  !fresh?.checklist ||
                  Boolean(
                    latestRun && reviewedBase && latestRun !== reviewedBase
                  );
              } catch {
                // transient read failure — keep waiting, the sweep below
                // still runs either way
              }
              if (settled || attempt === 7) break;
              // eslint-disable-next-line no-await-in-loop
              await new Promise((resolve) => setTimeout(resolve, 1500));
            }
          }
          await invalidateVero1StatusReads(dealId);
        } finally {
          exitVero1Settling(dealId);
        }
      }
      if (successMessage) {
        openNotification(successMessage, NOTIFICATIONS.info);
      }
    },
    onError: (error: {
      statusCode?: number;
      // one ApiError shape serves every mutation: conflicts carry code,
      // validation rejections carry error — both ride the same object
      errors?: {
        code?: string;
        state?: string;
        error?: { label?: string[] } | string[];
      };
      response?: {
        status?: number;
        data?: { code?: string; state?: string; error?: unknown };
      };
    }) => {
      // the API layer throws ApiError carrying statusCode; raw axios failures
      // carry response.status — recovery must read whichever arrived
      const status = error?.statusCode ?? error?.response?.status;
      const code = error?.errors?.code ?? error?.response?.data?.code;
      if (isRunInProgressConflict(error)) {
        // the prior review was set aside and a fresh screening kicked —
        // refresh everything; no toast, this is the designed path
        queryCache.invalidateQueries(key);
        enterVero1Settling(dealId);
        invalidateVero1StatusReads(dealId).finally(() =>
          exitVero1Settling(dealId)
        );
        return;
      }
      // conflict / gone: the draft moved under the agent (another edit,
      // submit, or discard elsewhere). The cached expected_updated_at token is
      // stale, so retries would fail forever — refetch to recover (spec §9)
      if (status === 409 || status === 404) {
        queryCache.invalidateQueries(key);
        // a closed review is not a re-appliable conflict: telling the agent to
        // re-apply invites a retry that can only fail again. The panel owns
        // announcing the lock, so say nothing here rather than twice
        const closed =
          code === Vero1ConflictCode.DealDecisioned ||
          code === Vero1ConflictCode.NotADraft;
        if (!conflictHandledInline && !closed) {
          openNotification(
            "This draft changed since you loaded it — refreshed to the latest version. Re-apply your change.",
            NOTIFICATIONS.error
          );
        }
        return;
      }
      if (validationHandledInline && status === 400) return;
      const messages = ([] as unknown[]).concat(
        (error?.errors?.error as unknown[]) ?? [],
        (error?.response?.data?.error as unknown[]) ?? []
      );
      if (
        status === 400 &&
        messages.some((m) => typeof m === "string" && /already open/i.test(m))
      ) {
        queryCache.invalidateQueries(key);
      }
      openNotification(errorMessage, NOTIFICATIONS.error);
    },
  });
};

export const useStartVero1Checklist = (dealId: Key) => {
  const [start, { isLoading }] = useChecklistMutation(
    dealId,
    () => startVero1Checklist(dealId),
    // starting over a submitted review sets it aside (2026-08-28) — the
    // deal's banner state changes with it, so the deal must refetch
    { errorMessage: "Failed to start the review", invalidatesDeal: true }
  );
  return { startChecklist: start, isStartingChecklist: isLoading };
};

export const useRefreshVero1Checklist = (dealId: Key) => {
  const [refresh, { isLoading }] = useChecklistMutation(
    dealId,
    () => refreshVero1Checklist(dealId),
    { errorMessage: "Failed to refresh the review" }
  );
  return { refreshChecklist: refresh, isRefreshingChecklist: isLoading };
};

export const useCarryForwardVero1Checklist = (dealId: Key) => {
  const [carryForward, { isLoading }] = useChecklistMutation(
    dealId,
    () => carryForwardVero1Checklist(dealId),
    { errorMessage: "Failed to carry the previous review forward" }
  );
  return { carryForward, isCarryingForward: isLoading };
};

export const useAddVero1ChecklistItem = (dealId: Key) => {
  const [addItem, { isLoading }] = useChecklistMutation<Vero1CustomItemInput>(
    dealId,
    (item) => addVero1ChecklistItem(dealId, item),
    // the form renders duplicate/already-included 400s inline — a generic
    // toast on top would double-message the agent
    { errorMessage: "Failed to add the item", validationHandledInline: true }
  );
  return { addItem, isAddingItem: isLoading };
};

/**
 * The autosave write path (spec §09). The merge follows the same only-advance
 * rules freshest() applies to full payloads: a slim response that lost the
 * race to a fresher poll is dropped whole — never a half-applied row beside
 * an unmoved token — and replace-only merging is the identity guard (a
 * discarded-and-restarted draft carries new row pks, so a stale response
 * finds nothing to replace). The poll keeps its full fetch.
 */
export const useAutosaveVero1ItemEdit = (dealId: Key) => {
  const key = VERO1_QUERY_KEYS.getVero1Checklist(dealId);
  const [editItemSlim] = useMutation(
    ({ itemId, edit }: { itemId: Key; edit: Vero1ItemEditRequest }) =>
      editVero1ChecklistItemSlim(dealId, itemId, edit),
    {
      onSuccess: (data: {
        item: Vero1ChecklistItemPayload;
        updatedAt: string;
      }) => {
        if (data?.item) {
          queryCache.setQueryData(
            key,
            (prev: Vero1ChecklistPayload | undefined) => {
              if (!prev?.checklist) return prev as Vero1ChecklistPayload;
              const cachedAt = prev.checklist.updatedAt;
              const cachedRow = prev.checklist.items.find(
                (row) => String(row.id) === String(data.item.id)
              );
              const advances =
                !cachedAt ||
                !data.updatedAt ||
                new Date(data.updatedAt) >= new Date(cachedAt);
              if (!cachedRow || !advances) return prev;
              // an older in-flight slim reply can carry a stale row even when the
              // draft token advances — gate the swap on the item's own stamp
              const rowRegresses =
                cachedRow.lastEditedAt &&
                data.item.lastEditedAt &&
                new Date(data.item.lastEditedAt) <
                  new Date(cachedRow.lastEditedAt);
              if (rowRegresses) return prev;
              return {
                ...prev,
                checklist: {
                  ...prev.checklist,
                  items: prev.checklist.items.map((row) =>
                    String(row.id) === String(data.item.id) ? data.item : row
                  ),
                  updatedAt: data.updatedAt,
                },
              };
            }
          );
        }
        queryCache.invalidateQueries([APPLICATION_AUDITS_QUERY, dealId]);
      },
      onError: (error: {
        statusCode?: number;
        response?: { status?: number };
      }) => {
        // a stale token only recovers through a refetch; the row banner owns
        // the messaging
        const status = error?.statusCode ?? error?.response?.status;
        if (status === 409 || status === 404) {
          queryCache.invalidateQueries(key);
        }
      },
    }
  );
  return editItemSlim;
};

export const useEditVero1ChecklistItem = (dealId: Key) => {
  const [editItem, { isLoading }] = useChecklistMutation<{
    itemId: Key;
    edit: Vero1ItemEditRequest;
  }>(
    dealId,
    ({ itemId, edit }) => editVero1ChecklistItem(dealId, itemId, edit),
    { conflictHandledInline: true, errorMessage: "Failed to save the item" }
  );
  return { editItem, isEditingItem: isLoading };
};

export const useSubmitVero1Checklist = (dealId: Key) => {
  const [submit, { isLoading }] = useChecklistMutation<Vero1SubmitInput>(
    dealId,
    (payload) => submitVero1Checklist(dealId, payload),
    {
      awaitDerivedRun: true,
      conflictHandledInline: true,
      errorMessage: "Failed to submit the review",
      invalidatesDeal: true,
      successMessage: "VERO1 review submitted.",
    }
  );

  /** v2 fires mutate-level callbacks beside the hook's, not after — so the
   *  caller's onSuccess (which closes the panel) is re-sequenced behind the
   *  awaited deal invalidation. throwOnError so a failure rejects rather than
   *  resolving to undefined: the caller's onError owns the 409 conflict UI. */
  const submitChecklist = (
    payload: Vero1SubmitInput,
    callbacks?: {
      onSuccess?: () => void;
      onError?: (error: unknown) => void;
    }
  ) =>
    submit(payload, { throwOnError: true })
      .then(() => callbacks?.onSuccess?.())
      .catch((error) => callbacks?.onError?.(error));

  return { submitChecklist, isSubmittingChecklist: isLoading };
};

export const useDeleteVero1ChecklistItem = (dealId: Key) => {
  const [deleteItem, { isLoading }] = useChecklistMutation<Key>(
    dealId,
    (itemId) => deleteVero1ChecklistItem(dealId, itemId),
    // the row is agent-added and draft-only; a 409 (draft moved / already
    // gone) refetches like every other mutation, no bespoke toast
    { conflictHandledInline: true, errorMessage: "Failed to remove the item" }
  );
  return { deleteItem, isDeletingItem: isLoading };
};

export const useDiscardVero1Checklist = (dealId: Key) => {
  const [discard, { isLoading }] = useChecklistMutation(
    dealId,
    () => discardVero1Checklist(dealId),
    // discard can revert the deal status; the restore may re-derive, so
    // hold the settle for the fresh run like submit does
    {
      awaitDerivedRun: true,
      errorMessage: "Failed to discard the draft",
      invalidatesDeal: true,
    }
  );
  return { discardChecklist: discard, isDiscardingChecklist: isLoading };
};
